import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, CheckCircle2, ArrowRight } from 'lucide-react';
import { fetchJSON } from '../../utils/api';
import { formatDate, formatDateShort } from '../../utils/dateFormat';
import { engagementTag } from '../../utils/engagementName';
import { flowLabel } from '../workflow/constants';
import WorkflowStatusBadge from '../workflow/WorkflowStatusBadge';
import EngagementStageBadge from '../workflow/EngagementStageBadge';
import NowName, { nowLabel } from '../shared/NowName';
import { bandOf, BAND_BY_KEY } from '../lnd/complianceBands';
// The upcoming-session row is shared with My Records, so a session reads the
// same in both places (docs/modules/my-records.md).
import { TeachingRow, DateChip, MetaLine, todayISO, dayAfter, fmtHours } from '../lnd/SessionRow';

// Overview personal feed — the "what needs me / what's mine" blocks.
// One fetch (GET /api/overview), one
// card per block; a block with nothing to say disappears entirely.
//
// To add a future block: add an entry to buildSections() below — title,
// subtitle, rows — and (if it needs new data) extend /api/overview. The shell
// (card, count pill, show-all folding, empty-section hiding) is shared.

const INITIAL_ROWS = 5; // rows shown before "Show all"

// ── Column balancing ────────────────────────────────────────────────────────
// Pixel heights of the card parts, measured off the rendered page. They are
// only ever compared against each other — the split needs to know which column
// is currently shorter, never the real layout — but they do have to stay in
// proportion: under-estimating a body is how a column ends up holding one short
// card while the other holds everything else.
const CARD_HEADER_H = 72;    // title + subtitle + divider
const ROW_H = 58;            // <Row> — primary + secondary line
const SESSION_ROW_H = 66;    // <SessionRow> — date chip + meta line, taller
const PROGRESS_BODY_H = 118; // the training-hours stacked bar body
const SHOW_ALL_H = 33;       // the "Show all N" footer
const COLUMN_GAP_H = 24;     // gap-6 between cards in a column

// Tailwind's `xl` — the breakpoint the feed goes two-column at.
const XL_QUERY = '(min-width: 1280px)';

// A single list row. `to` makes the whole row clickable; `right` renders at
// the far end (chip / date / CTA); rows without `to` are plain.
const Row = ({ to, primary, secondary, right }) => {
  const body = (
    <>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-slate-800 truncate">{primary}</p>
        {secondary && <p className="text-xs text-slate-500 truncate mt-0.5">{secondary}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {right}
        {to && <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500" />}
      </div>
    </>
  );
  const cls = 'flex items-center gap-3 px-5 py-2.5';
  return to ? (
    <Link to={to} className={`${cls} group hover:bg-slate-50 transition-colors`}>{body}</Link>
  ) : (
    <div className={cls}>{body}</div>
  );
};

// Small inline CTA used by the training-action rows ("File absence" /
// "Give feedback") — navigates without waiting for the row click affordance.
const RowCta = ({ to, children }) => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(to); }}
      className="px-2.5 py-1 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700"
    >
      {children}
    </button>
  );
};

// The deadline chip: how many days to (or past) the reminder date. Amber for
// both "due soon" and "overdue" — the dashboard's not-met colour is amber, not
// red — with the overdue state a step stronger and said in words.
const DeadlineChip = ({ daysLeft }) => {
  const overdue = daysLeft < 0;
  const text = overdue
    ? `Overdue ${Math.abs(daysLeft)} d`
    : daysLeft === 0 ? 'Due today' : `${daysLeft} d left`;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
      overdue ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'bg-amber-50 text-amber-700'
    }`}>
      {text}
    </span>
  );
};

// What the reminder date IS at this stage of the engagement (fs_signing before
// the Signed Financial Statements record exists, archival after) — worded, never colour-only.
const DEADLINE_KIND_LABEL = {
  fs_signing: 'Expected FS signing',
  archival: 'Archival deadline',
};

// One training session as it concerns me — upcoming, or over and still
// owing something (absence to file, feedback to give, quiz to pass). One row
// per session whatever it needs; the whole row opens the session page, each
// owed action has its own button. Replaces the former Upcoming training and
// Training follow-ups cards, which listed the same session twice on its day.
const ACTION_META = {
  absence: { label: 'File Absence', text: 'Not attended — declare your absence' },
  feedback: { label: 'Give Feedback', text: 'Attended — feedback not submitted yet' },
  quiz: { label: 'Take Quiz', text: 'Quiz not taken yet' },
};
const TrainingRow = ({ row, today, tomorrow }) => {
  const info = row.info; // the upcoming envelope (time / trainer / venue), when the session is still to come
  const owes = row.actions.length > 0;
  const when = info && row.date === today ? 'Today' : info && row.date === tomorrow ? 'Tomorrow' : null;
  return (
    <Link to={`/my/sessions/${row.id}`} className="block hover:bg-slate-50 transition-colors">
      <div className="flex items-start gap-3 px-5 py-3">
        <DateChip date={row.date} today={today} tone={owes ? 'amber' : 'violet'} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium text-slate-800 truncate">{row.courseTitle}</p>
            {when && (
              <span className="shrink-0 px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[11px] font-semibold">{when}</span>
            )}
          </div>
          {info ? (
            <MetaLine items={[
              info.startEndTime && { text: info.startEndTime },
              { text: info.trainerNames || 'Trainer not assigned', muted: !info.trainerNames },
              { text: info.venueName || 'Venue not specified', muted: !info.venueName },
            ]} />
          ) : (
            <MetaLine items={[{ text: `Session on ${formatDate(row.date)}`, muted: true }]} />
          )}
          {owes && (
            <MetaLine className="mt-0.5" items={row.actions.map((a) => ({ text: a.text, warn: true }))} />
          )}
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1.5">
          {row.actions.map((a) => <RowCta key={a.key} to={a.href}>{a.label}</RowCta>)}
          {!owes && info && info.hours != null && (
            <span className="mt-0.5 px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-xs font-medium tabular-nums">
              {fmtHours(info.hours)} h
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

// Upcoming + the three nags, folded into one row per session. Rows that owe
// something come first (latest session first), then what is still to come.
const buildTrainingRows = (training) => {
  const byId = new Map();
  const ensure = (s) => {
    if (!byId.has(s.id)) byId.set(s.id, { id: s.id, courseTitle: s.courseTitle, date: s.date, info: null, actions: [] });
    return byId.get(s.id);
  };
  (training.upcoming || []).forEach((s) => { ensure(s).info = s; });
  (training.absenceNeeded || []).forEach((s) => ensure(s).actions.push({ key: 'absence', href: s.href, ...ACTION_META.absence }));
  (training.feedbackNeeded || []).forEach((s) => ensure(s).actions.push({ key: 'feedback', href: s.href, ...ACTION_META.feedback }));
  (training.quizNeeded || []).forEach((s) => ensure(s).actions.push({
    key: 'quiz',
    href: s.href,
    label: s.attempts > 0 ? 'Retake Quiz' : 'Take Quiz',
    text: `Quiz "${s.quizTitle}" ${s.attempts > 0 ? `not passed after ${s.attempts} attempt${s.attempts === 1 ? '' : 's'}` : 'not taken yet'}`,
  }));
  return [...byId.values()].sort((a, b) => {
    const ao = a.actions.length > 0 ? 0 : 1;
    const bo = b.actions.length > 0 ? 0 : 1;
    if (ao !== bo) return ao - bo;
    if (ao === 0) return a.date < b.date ? 1 : a.date > b.date ? -1 : 0; // owed: latest first
    return a.date < b.date ? -1 : a.date > b.date ? 1 : 0;              // upcoming: soonest first
  });
};

// A session I am booked to teach — same page, trainer tools on it.
const TeachingLink = ({ session, today, tomorrow }) => (
  <Link to={session.href || `/my/sessions/${session.id}`} className="block hover:bg-slate-50 transition-colors">
    <TeachingRow session={session} today={today} tomorrow={tomorrow} />
  </Link>
);

// `right` renders at the header's far end — the count pill for list sections,
// a status badge for the progress card.
// Headers are text only; the title says what the block is.
const SectionCard = ({ title, subtitle, right, children }) => (
  <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="flex items-center gap-3 px-5 py-3.5 bg-slate-100/80 border-b border-slate-200">
      <div className="min-w-0 flex-1">
        <h3 className="text-[15px] font-semibold text-slate-900 tracking-tight">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="shrink-0">{right}</div>
    </div>
    {children}
  </section>
);

const CountPill = ({ count }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-white text-slate-700 ring-1 ring-slate-200">
    {count}
  </span>
);

// Count + a way through to the full list in My Records. The Overview only ever
// shows what is CURRENT (ongoing engagements, sessions still to come); the
// complete record — including finished work — lives one click away.
const SectionLink = ({ to, count }) => (
  <div className="flex items-center gap-2">
    <CountPill count={count} />
    <Link to={to} className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 whitespace-nowrap">
      View All <ArrowRight className="w-3.5 h-3.5" />
    </Link>
  </div>
);

// ── My training hours ───────────────────────────────────────────────────────
// One stacked bar for the current training year. Reading order = the compliance
// vocabulary (complianceBands): only internal MANDATORY hours count against the
// target — the bar allocates the first `max(target, mandatory)` of its width to
// that race (fill vs. track, dashed marker at the target), then appends the two
// reference buckets (non-mandatory indigo, External CPD green — the Mandatory
// Compliance table's colours) after it, so they can never read as closing the
// gap. Colour never carries the meaning alone: the badge, the headline figure
// and the legend all say it in words and numbers.
const TrainingProgressBody = ({ progress }) => {
  const { target, mandatoryHours, nonMandatoryHours, externalHours } = progress;
  const met = bandOf(mandatoryHours, target) === 'met';
  const gap = Math.max(0, target - mandatoryHours);
  const mandatoryZone = Math.max(target, mandatoryHours);
  const scale = Math.max(1, mandatoryZone + nonMandatoryHours + externalHours);
  const pct = (v) => `${(v / scale) * 100}%`;
  const mandatoryCls = met ? 'bg-emerald-600' : 'bg-amber-500';

  const legend = [
    { label: 'Mandatory', hours: mandatoryHours, dot: mandatoryCls },
    ...(gap > 0 ? [{ label: 'Still to go', hours: gap, dot: 'bg-slate-200' }] : []),
    { label: 'Non-mandatory', hours: nonMandatoryHours, dot: 'bg-indigo-500' },
    { label: 'External CPD', hours: externalHours, dot: 'bg-green-500' },
  ];

  return (
    <div className="px-5 py-4">
      <p className="text-sm">
        <span className={`text-2xl font-bold tabular-nums ${met ? 'text-emerald-700' : 'text-slate-900'}`}>
          {fmtHours(mandatoryHours)}
        </span>
        <span className="text-slate-500"> / {fmtHours(target)} h mandatory</span>
        {gap > 0 && <span className="text-amber-700 font-medium"> · {fmtHours(gap)} h to go</span>}
      </p>

      <div className="relative mt-3 mb-1">
        <div className="h-3 rounded-full bg-slate-100 relative overflow-hidden">
          <div className={`absolute inset-y-0 left-0 ${mandatoryCls}`} style={{ width: pct(mandatoryHours) }} />
          <div className="absolute inset-y-0 bg-indigo-500" style={{ left: pct(mandatoryZone), width: pct(nonMandatoryHours) }} />
          <div className="absolute inset-y-0 bg-green-500 rounded-r-full" style={{ left: pct(mandatoryZone + nonMandatoryHours), width: pct(externalHours) }} />
        </div>
        {/* Dashed marker = the mandatory target line, overshooting the bar
            like a goal post (visible whether the fill is short of it or past
            it). */}
        {target > 0 && (
          <div
            className="absolute -top-1 -bottom-1 border-l-2 border-dashed border-slate-500"
            style={{ left: `calc(${(target / scale) * 100}% - 1px)` }}
            title={`Target: ${fmtHours(target)} h internal mandatory`}
          />
        )}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
        {legend.map(({ label, hours, dot }) => (
          <span key={label} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
            <span className={`w-2.5 h-2.5 rounded-sm ${dot}`} />
            {label} <span className="font-medium tabular-nums">{fmtHours(hours)} h</span>
          </span>
        ))}
      </div>
    </div>
  );
};

// Folding list body: first INITIAL_ROWS rows, then a "Show all" toggle.
const FoldingList = ({ rows }) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, INITIAL_ROWS);
  return (
    <>
      <div className="divide-y divide-slate-100">{visible}</div>
      {rows.length > INITIAL_ROWS && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full px-5 py-2 text-xs font-medium text-blue-600 hover:bg-slate-50 border-t border-slate-100 text-left"
        >
          {expanded ? 'Show Less' : `Show All ${rows.length}`}
        </button>
      )}
    </>
  );
};

// Estimated height of one card, folded (an expanded "Show all" only ever grows
// its own column, so it must not feed the split — otherwise the layout would
// reshuffle under the reader's cursor).
const weightOf = (s) => {
  const rows = s.rows || [];
  const body = s.bodyH || Math.min(rows.length, INITIAL_ROWS) * (s.rowH || ROW_H);
  return CARD_HEADER_H + body + (rows.length > INITIAL_ROWS ? SHOW_ALL_H : 0);
};

// Two independent columns instead of a two-column grid: a CSS grid stretches
// every card in a row to the tallest one, which would leave the short "My
// engagements" card trailing a wall of white space under a long "Upcoming
// training".
//
// Greedy balance — walk the sections in priority order and drop each one on the
// currently shorter column (ties go left), so the columns end level while the
// sections that matter most still land near the top. Cards can swap sides as
// the data changes; that is the price of never stranding a column.
const splitColumns = (list) => {
  const columns = [[], []];
  const heights = [0, 0];
  list.forEach((s) => {
    const i = heights[1] < heights[0] ? 1 : 0;
    columns[i].push(s);
    heights[i] += weightOf(s) + COLUMN_GAP_H;
  });
  return columns;
};

// Below `xl` the feed is a single column and the sections must keep their
// original priority order — splitting there would push "My engagements" to the
// very bottom. Driven off matchMedia rather than a `hidden xl:block` pair so
// only one tree is ever mounted: two would duplicate every FoldingList's
// expanded state and every stage-badge popover.
const useIsWide = () => {
  const [wide, setWide] = useState(() => window.matchMedia(XL_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(XL_QUERY);
    const onChange = (e) => setWide(e.matches);
    setWide(mq.matches); // in case it changed between first render and effect
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return wide;
};

const renderSection = ({ key, rows, body, right, rowH, bodyH, ...meta }) => (
  <SectionCard
    key={key}
    {...meta}
    right={right || <CountPill count={rows.length} />}
  >
    {body || <FoldingList rows={rows} />}
  </SectionCard>
);

const OverviewSections = () => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    fetchJSON('/api/overview')
      .then((d) => { if (active) setData(d); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, []);

  const sections = useMemo(() => {
    if (!data) return [];
    const reviewQueue = data.reviewQueue || [];
    const myRequests = data.myRequests || [];
    const training = data.training || {};
    const trainingRows = buildTrainingRows(training);
    const teaching = training.teaching || [];
    const engagements = data.engagements || [];
    const deadlines = data.deadlines || [];

    const list = [];

    if (reviewQueue.length > 0) {
      list.push({
        key: 'review',
        title: 'Awaiting Your Review',
        subtitle: 'Requests waiting on you, oldest first.',
        rows: reviewQueue.map((r) => (
          <Row
            key={`${r.kind}-${r.id}`}
            to={r.href}
            primary={<><span className="font-medium">{r.requester}</span> — {flowLabel(r.kind, r.qcType)}</>}
            secondary={`${r.requestNo} · submitted ${formatDate(r.submittedAt)}`}
          />
        )),
      });
    }

    if (myRequests.length > 0) {
      list.push({
        key: 'mine',
        title: 'My Requests in Progress',
        subtitle: 'Everything you submitted that is not approved yet — and your saved drafts.',
        rows: myRequests.map((r) => (
          <Row
            key={`${r.kind}-${r.id}`}
            to={r.href}
            primary={<><span className="font-medium">{flowLabel(r.kind, r.qcType)}</span> {r.requestNo}</>}
            secondary={r.status === 'awaiting_info'
              ? 'Returned to you — make the changes and re-submit it, or delete it.'
              : r.status === 'draft'
                ? `Draft saved ${formatDate(r.submittedAt)} — not submitted yet; open it to continue.`
                : `Submitted ${formatDate(r.submittedAt)}`}
            right={<WorkflowStatusBadge status={r.status} size="chip" />}
          />
        )),
      });
    }

    if (deadlines.length > 0) {
      list.push({
        key: 'deadlines',
        title: 'Upcoming Deadlines',
        subtitle: 'Expected FS signing and archival deadlines on your engagements — most urgent first.',
        rows: deadlines.map((d) => (
          <Row
            key={d.id}
            to={`/my/engagements/${d.id}`}
            primary={<NowName value={d.entityName || d.engagementName} now={d.entityNameNow} />}
            secondary={[
              nowLabel(d.clientGroup, d.clientGroupNow),
              `FYE ${formatDateShort(d.yearEnd)}`,
              engagementTag(d.serviceProvided, d.engagementSeq, d.serviceDescription),
              `${DEADLINE_KIND_LABEL[d.kind] || 'Deadline'} ${formatDateShort(d.date)}`,
            ].filter(Boolean).join(' · ')}
            right={<DeadlineChip daysLeft={d.daysLeft} />}
          />
        )),
      });
    }

    if (trainingRows.length > 0) {
      const today = todayISO();
      const tomorrow = dayAfter(today);
      const owed = trainingRows.filter((r) => r.actions.length > 0).length;
      list.push({
        key: 'training',
        title: 'My Training',
        subtitle: owed > 0
          ? `${owed} session${owed === 1 ? '' : 's'} still need${owed === 1 ? 's' : ''} something from you — these stay here until you act.`
          : 'Sessions you are expected to attend — open one for the full picture.',
        right: <SectionLink to="/my?tab=sessions" count={trainingRows.length} />,
        rowH: SESSION_ROW_H + 10,
        actionable: owed > 0,
        rows: trainingRows.map((r) => <TrainingRow key={r.id} row={r} today={today} tomorrow={tomorrow} />),
      });
    }

    // Sessions I run that are not closed yet — the trainer's to-do: put up
    // the materials, write the quiz. Opens the session page where both happen.
    if (teaching.length > 0) {
      const today = todayISO();
      const tomorrow = dayAfter(today);
      list.push({
        key: 'teaching',
        title: 'My Teaching',
        subtitle: 'Sessions you are booked to run — open one to upload materials and write the quiz.',
        right: <SectionLink to="/my?tab=sessions" count={teaching.length} />,
        rowH: SESSION_ROW_H + 24,
        actionable: true,
        rows: teaching.map((s) => (
          <TeachingLink key={s.id} session={s} today={today} tomorrow={tomorrow} />
        )),
      });
    }

    if (training.progress) {
      const p = training.progress;
      const band = BAND_BY_KEY[bandOf(p.mandatoryHours, p.target)];
      list.push({
        key: 'training-progress',
        title: 'My Training Hours',
        subtitle: `Training year ${formatDate(p.periodStart)} – ${formatDate(p.periodEnd)}`,
        right: (
          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full border ${band.badge}`}>
            {band.short}
          </span>
        ),
        body: <TrainingProgressBody progress={p} />,
        bodyH: PROGRESS_BODY_H,
      });
    }

    if (engagements.length > 0) {
      list.push({
        key: 'engagements',
        title: 'My Engagements',
        subtitle: 'Ongoing engagements you are on the team of, or review as EQR.',
        // Everyone opens their own engagements — the
        // detail page is read-only and membership-scoped, so no key is checked
        // here. Completed work lives in the My Records list behind "View all".
        right: <SectionLink to="/my?tab=engagements" count={engagements.length} />,
        rows: engagements.map((e) => (
          <Row
            key={e.id}
            to={`/my/engagements/${e.id}`}
            primary={<NowName value={e.entityName || e.engagementName} now={e.entityNameNow} />}
            secondary={[
              nowLabel(e.clientGroup, e.clientGroupNow),
              `FYE ${formatDateShort(e.yearEnd)}`,
              engagementTag(e.serviceProvided, e.engagementSeq, e.serviceDescription),
            ].filter(Boolean).join(' · ')}
            // 兩件不同的事並排：Current stage 說「卡在
            // 哪一關」（推導、唯讀），Status 說「這案子還活著嗎」（主檔欄位、
            // 逾期判斷的入場券）。只放 status 不夠——進行中的案子過了
            // 首次送出就恆為 Active，那個 badge 等於沒有資訊。
            right={(
              <span className="flex items-center gap-2">
                {/* 被點名為 EQR 的案不是「我的團隊」的案——標出來，讀者才知道
                    它為什麼在這裡（My Records 的清單有整欄 Your role）。 */}
                {(e.myRoles || []).includes('eqr') && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-violet-50 text-violet-700 whitespace-nowrap">EQR</span>
                )}
                {/* 在審時點得開「在等誰簽核」——整列是個
                    Link，觸發器自己吃掉點擊，不會順便把人帶去詳情頁。 */}
                <EngagementStageBadge stage={e.currentStage} engagementId={e.id} />
                <span className="text-xs text-slate-500 whitespace-nowrap">{e.status}</span>
              </span>
            )}
          />
        )),
      });
    }

    return list;
  }, [data]);

  const wide = useIsWide(); // before the early returns — hook order is fixed

  // The feed IS the Overview —
  // a load failure must say so instead of leaving a blank page.
  if (failed) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-4 text-sm text-slate-500">
        Couldn't load your overview — refresh the page to try again.
      </div>
    );
  }
  if (!data) return null; // still loading (local API — resolves in a beat)

  // "Caught up" only speaks for the blocks that demand something — a review
  // queue, my open requests, deadlines, a session owing an action, a session
  // to teach. Purely informational blocks (upcoming sessions, engagements)
  // don't count against it.
  const ALWAYS_ACTIONABLE = ['review', 'mine', 'deadlines'];
  const allClear = sections.every((s) => !ALWAYS_ACTIONABLE.includes(s.key) && !s.actionable);

  return (
    <div className="mb-8">
      {allClear && (
        <div className="mb-6 flex items-center gap-2.5 bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-3.5">
          <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
          <p className="text-sm text-slate-600">
            All caught up.
          </p>
        </div>
      )}
      {sections.length > 0 && (wide ? (
        <div className="grid grid-cols-2 gap-6 items-start">
          {splitColumns(sections).map((column, i) => (
            <div key={i} className="flex flex-col gap-6">
              {column.map(renderSection)}
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-6">{sections.map(renderSection)}</div>
      ))}
    </div>
  );
};

export default OverviewSections;
